import Button from '@/components/Button'
import Icon from '@/components/Icon'
import { MUTATE_DATA } from '@/fragments/GravityForms'
import { faArrowRight } from '@fortawesome/sharp-solid-svg-icons'
import {
  handleCaptcha,
  handleInputChange,
  handleSubfieldsInputChange,
  useFieldValues,
  useOptionsState
} from '@/utilities/gravityForms/gfFieldsInputHandlers'
import { validateOnSubmit } from '@/utilities/gravityForms/gfFieldsValidation'
import { useMutation } from '@apollo/client'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import FormFields from '../FormFields'
import styles from './ContactForm.module.scss'

export default function Form(form) {
  const { submitButton, formFields, databaseId } = form.form
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { fieldValuesArray, fieldValues, clearForm, updateFieldValue } = useFieldValues(formFields)
  const { selectStates, handleFileChange, handleOptionClick, resetSelectStates, toggleDropdown, dropdownRefs } = useOptionsState()
  const [isModalOpen, setModalOpen] = useState(true)
  const [formError, setFormError] = useState(null)
  const router = useRouter()
  const isCitgoPage = router.asPath.includes('citgo')

  useEffect(() => {
    let timer
    if (isModalOpen) {
      timer = setTimeout(() => setModalOpen(false), 2000)
    }
    return () => clearTimeout(timer)
  }, [isModalOpen])

  const clearContactForm = () => {
    clearForm()
    resetSelectStates()
  }

  const [updateData] = useMutation(MUTATE_DATA)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const isValid = validateOnSubmit(formFields, fieldValues)

    if (!isValid) {
      e.stopPropagation()
      setFormError('Please complete all required fields correctly.')
      return false
    }

    try {
      setIsSubmitting(true)
      await updateData({
        variables: { fieldValues: fieldValuesArray, databaseId }
      })
      setIsFormSubmitted(true)
      setModalOpen(true)
      clearContactForm()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.contactFooterForm} data-aos="fade-up" id="footer-contact-form" data-cy="contact-form-section">
      <div className={styles.leftContent}>
        <div className={styles.h2Wrapper}>
          <h2>Let&apos;s make something great.</h2>
          {isCitgoPage && (
            <p className={styles.citgoMessage}>
              <span>
                Call <a href="tel:3147273600">314.727.3600</a>
              </span>{' '}
              or use the form below to connect with us.
            </p>
          )}
        </div>
      </div>
      <div className={styles.GravityForm}>
        {isFormSubmitted ? (
          <div className={styles.confirmationMessage}>
            <h3>Thank You for Your Submission!</h3>
            <p>
              We appreciate your interest in partnering with us. Our team will review your information and get in touch with you soon to
              discuss potential collaboration opportunities.
            </p>
          </div>
        ) : (
          <>
            <h3>Ready to collaborate?</h3>
            <form onSubmit={handleSubmit} data-cy="contact-form">
              <FormFields
                formFields={formFields}
                formState={{
                  fieldValues,
                  fieldValuesArray,
                  updateFieldValue,
                  selectStates,
                  dropdownRefs
                }}
                handlers={{
                  handleInputChange,
                  handleSubfieldsInputChange,
                  handleCaptcha,
                  handleFileChange,
                  handleOptionClick,
                  toggleDropdown
                }}
              />

              {formError && (
                <div className="error-container">
                  <div className={styles.formError}>
                    <p className="error-text" data-cy="error-summary">
                      {formError}
                    </p>
                  </div>
                </div>
              )}
              <div className={styles.submit}>
                <Button
                  className={`${styles.formButtons} ${isSubmitting ? styles.disabled : ''}`}
                  data-cy="submit-button"
                  disabled={isSubmitting}
                >
                  {submitButton.text}
                  <Icon icon={faArrowRight} />
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
